import { z } from 'zod';
import { TASK_COMPLETION_POINTER_AREAS, type TaskCompletionPointer } from '../constants/business/task';

/**
 * Completion evidence for tasks the platform closes from data rather than from a tick
 * (Clara, CUR-1158). A task that carries a `completionEvidence` spec can only be completed
 * with a verified row of the spec's model anchored to the task's claim; manual completion is
 * refused with the spec's reason and a pointer to where the work happens.
 */

/** Models that can serve as completion evidence. Grows per slice; every entry is an audited claim child. */
export const TASK_EVIDENCE_MODELS = ['Reserve'] as const;
export type TaskEvidenceModel = (typeof TASK_EVIDENCE_MODELS)[number];

/** The column an evidence row must share with the task for the evidence to count. */
export const TASK_EVIDENCE_ANCHORS = ['claimId'] as const;
export type TaskEvidenceAnchor = (typeof TASK_EVIDENCE_ANCHORS)[number];

export const taskCompletionPointerSchema = z
    .object({ area: z.enum(TASK_COMPLETION_POINTER_AREAS) })
    .strict() satisfies z.ZodType<TaskCompletionPointer>;

export const taskCompletionEvidenceSpecSchema = z
    .object({
        kind: z.literal('row-exists'),
        model: z.enum(TASK_EVIDENCE_MODELS),
        anchor: z.enum(TASK_EVIDENCE_ANCHORS),
        /** Shown to a handler who tries to complete the task by hand. */
        reason: z.string().trim().min(1).max(500),
        pointer: taskCompletionPointerSchema.optional(),
    })
    .strict();
export type TaskCompletionEvidenceSpec = z.infer<typeof taskCompletionEvidenceSpecSchema>;

/** Decimal-string row id: a JSON column cannot carry a bigint. */
const evidenceRowIdSchema = z.string().regex(/^\d{1,19}$/);

/** What `TaskService.complete` records once the evidence row has been verified. */
export const taskCompletedEvidenceSchema = z
    .object({
        model: z.enum(TASK_EVIDENCE_MODELS),
        id: evidenceRowIdSchema,
        verifiedAt: z.iso.datetime(),
    })
    .strict();
export type TaskCompletedEvidence = z.infer<typeof taskCompletedEvidenceSchema>;

/** `getTask`'s answer to "may this be ticked by hand?", consumed by the task view. */
export type TaskManualCompletion =
    | { allowed: true }
    | { allowed: false; reason: string; pointer: TaskCompletionPointer | null };
